// src/lib/vitalReferenceBands.ts
// Which reference lines a vitals trend chart may draw, and for whom (KMC #34).
//
// Two sources, neither invented here:
//   * IP — the hospital's own `ip_alert_rules` (NEWS2-derived, tenant-
//     configurable, versioned, each with a stated clinical basis). Drawn from
//     the rows as currently configured, so a hospital that changed or disabled
//     a threshold sees its own numbers, or none.
//   * OPD — lib/vitalThresholds.ts's VITAL_LIMITS, the advisory display hints
//     Patient Records already tints tiles with. Labelled advisory, like there.
//
// ── Children ─────────────────────────────────────────────────────
// Both sets are ADULT numbers: the seeded IP rules say "Adult thresholds"
// (NEWS2 is not validated for children) and the OPD pulse/BP bands are adult
// ranges. There is no paediatric configuration in this system, so for a child
// — or a patient whose age is unknown — the chart draws NO bands at all rather
// than adult ones that would look authoritative and be wrong. It says so on
// screen instead. ADULT_MIN_AGE is the one number to change if the hospital
// adopts a different adult cut-off.
import { VITAL_LIMITS } from './vitalThresholds';

export type TrendMetric = 'bp' | 'pulse' | 'temp' | 'spo2' | 'weight' | 'bmi';
export type TempUnit = 'C' | 'F';

export interface ReferenceLineSpec {
  value: number;
  severity: 'warn' | 'critical';
  label: string;
}

export const ADULT_MIN_AGE = 18;

/** Completed years from DOB when known (more reliable than a typed age), else
 * the recorded age, else null. */
export function ageInYears(age: number | string | null | undefined, dob?: string | null, now = new Date()): number | null {
  if (dob) {
    const d = new Date(dob);
    if (!Number.isNaN(d.getTime())) {
      let years = now.getFullYear() - d.getFullYear();
      const m = now.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
      return years;
    }
  }
  const n = age === '' || age == null ? NaN : Number(age);
  return Number.isFinite(n) ? n : null;
}

/** Unknown age is NOT treated as adult — see the header. */
export function isAdult(age: number | string | null | undefined, dob?: string | null): boolean {
  const y = ageInYears(age, dob);
  return y != null && y >= ADULT_MIN_AGE;
}

// ── Temperature units ────────────────────────────────────────────
// vitals.temperature holds °F for OP rows (the Vitals stage, 80-115 °F) and
// °C for IP rows (the Case Sheet form, migration_ip_case_sheet.sql). No human
// temperature is above 50 °C or below 50 °F, so the value itself says which
// scale it is on — robust even for a visit-linked OP reading shown on the
// Case Sheet. The backend mirror is utils/vitalsTemperature.js — change both.
export function normalizeTemp(value: number, unit: TempUnit): number {
  const isF = value > 50;
  if (unit === 'C') return isF ? Math.round(((value - 32) * 5 / 9) * 10) / 10 : value;
  return isF ? value : Math.round((value * 9 / 5 + 32) * 10) / 10;
}

const toUnit = (celsius: number, unit: TempUnit) => (unit === 'C' ? celsius : Math.round((celsius * 9 / 5 + 32) * 10) / 10);
const fromF = (f: number, unit: TempUnit) => (unit === 'F' ? f : Math.round(((f - 32) * 5 / 9) * 10) / 10);

// ── IP: from ip_alert_rules ──────────────────────────────────────

interface RuleLike { rule_key: string; enabled: boolean; params: Record<string, number> }

const RULE_FOR: Partial<Record<TrendMetric, string>> = {
  spo2: 'vital_spo2_low',
  bp: 'vital_sbp_low',
  pulse: 'vital_hr_abnormal',
  temp: 'vital_temp_abnormal',
};

export function ipReferenceLines(rules: RuleLike[] | undefined, metric: TrendMetric, tempUnit: TempUnit): ReferenceLineSpec[] {
  const key = RULE_FOR[metric];
  const rule = key ? rules?.find(r => r.rule_key === key) : undefined;
  if (!rule || !rule.enabled) return [];
  const p = rule.params || {};
  const conv = (v: number) => (metric === 'temp' ? toUnit(v, tempUnit) : v);
  const prefix = metric === 'bp' ? 'SBP ' : '';
  const out: ReferenceLineSpec[] = [];
  if (p.red_below !== undefined) out.push({ value: conv(p.red_below), severity: 'critical', label: `${prefix}critical ≤${conv(p.red_below)}` });
  if (p.amber_below !== undefined) out.push({ value: conv(p.amber_below), severity: 'warn', label: `${prefix}<${conv(p.amber_below)}` });
  if (p.amber_above !== undefined) out.push({ value: conv(p.amber_above), severity: 'warn', label: `${prefix}>${conv(p.amber_above)}` });
  if (p.red_above !== undefined) out.push({ value: conv(p.red_above), severity: 'critical', label: `${prefix}critical ≥${conv(p.red_above)}` });
  return out;
}

// ── OPD: advisory hints ──────────────────────────────────────────

export function opdReferenceLines(metric: TrendMetric, tempUnit: TempUnit): ReferenceLineSpec[] {
  const L = VITAL_LIMITS;
  const w = (value: number, label: string): ReferenceLineSpec => ({ value, severity: 'warn', label });
  switch (metric) {
    case 'bp': return [w(L.bpSystolicHigh, `SBP >${L.bpSystolicHigh}`), w(L.bpDiastolicHigh, `DBP >${L.bpDiastolicHigh}`), w(L.bpSystolicLow, `SBP <${L.bpSystolicLow}`)];
    case 'pulse': return [w(L.pulseHigh, `>${L.pulseHigh}`), w(L.pulseLow, `<${L.pulseLow}`)];
    case 'temp': { const v = fromF(L.tempHighF, tempUnit); return [w(v, `>${v}°${tempUnit}`)]; }
    case 'spo2': return [w(L.spo2Low, `<${L.spo2Low}%`)];
    case 'bmi': return [w(L.bmiHigh, `≥${L.bmiHigh}`), w(L.bmiLow, `<${L.bmiLow}`)];
    default: return [];
  }
}
